import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import {
  Plus, Search, Send, ArrowLeft, X, Users, MessageSquare,
  ChevronDown, Loader2, Edit3, Bell, Check, CheckCheck,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useNotifikationer } from "@/contexts/NotifikationerContext";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ─── Hjælpere ─────────────────────────────────────────────────────────────────

function datoLabel(dato) {
  const d = new Date(dato);
  const idag = new Date();
  const igar = new Date(); igar.setDate(igar.getDate() - 1);
  const same = (a, b) =>
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();
  if (same(d, idag)) return "I dag";
  if (same(d, igar)) return "I går";
  return d.toLocaleDateString("da-DK", { day: "numeric", month: "long" });
}

function klokkeslet(ts) {
  if (!ts) return "";
  return new Date(ts).toLocaleTimeString("da-DK", { hour: "2-digit", minute: "2-digit" });
}

function sidstAktivLabel(ts) {
  if (!ts) return "";
  const diffMin = Math.floor((Date.now() - new Date(ts)) / 60000);
  if (diffMin < 1) return "Nu";
  if (diffMin < 60) return `${diffMin} min`;
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24) return `${diffH} t`;
  const diffD = Math.floor(diffH / 24);
  if (diffD < 7) return `${diffD} d`;
  return new Date(ts).toLocaleDateString("da-DK", { day: "numeric", month: "short" });
}

function fmtTid(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const diffMin = Math.floor((Date.now() - d) / 60000);
  const diffH = Math.floor(diffMin / 60);
  const diffD = Math.floor(diffH / 24);
  if (diffMin < 1) return "Lige nu";
  if (diffMin < 60) return `${diffMin} min siden`;
  if (diffH < 24) return `${diffH} t siden`;
  if (diffD < 7) return `${diffD} dag${diffD !== 1 ? "e" : ""} siden`;
  return d.toLocaleDateString("da-DK", { day: "numeric", month: "short" });
}

const NOTIF_IKON = {
  besked: "💬",
  ny_vagt: "📅",
  vagtaendring: "🔄",
  ferie_godkendt: "✅",
  ferie_afvist: "❌",
};

// ─── Dato-separator ───────────────────────────────────────────────────────────

function DatoSep({ label }) {
  return (
    <div className="flex items-center gap-2 my-3 select-none">
      <div className="flex-1 border-t border-border" />
      <span className="text-[10px] text-muted-foreground px-2">{label}</span>
      <div className="flex-1 border-t border-border" />
    </div>
  );
}

// ─── Besked-boble ─────────────────────────────────────────────────────────────

function Boble({ besked, erMin, kanalMedlemmer, kanalMedlemmerNavne, erGruppe, minEmail }) {
  const laestAf = besked.laest_af || [];
  // Ekskludér afsenderen selv OG den indloggede bruger — kun andre tæller som "har læst"
  const andreLæste = laestAf.filter(
    (e) => e !== besked.afsender_email && e !== minEmail
  );
  const erLæst = andreLæste.length > 0;

  // Email → navn map fra kanal-medlemmer
  const navnMap = {};
  (kanalMedlemmer || []).forEach((email, i) => {
    navnMap[email] = (kanalMedlemmerNavne || [])[i] || email;
  });

  const læsteNavne = andreLæste.map((e) => navnMap[e] || e);
  const tooltipTekst = læsteNavne.join(", ");

  return (
    <div className={`flex flex-col mb-1 ${erMin ? "items-end" : "items-start"}`}>
      {!erMin && (
        <span className="text-[10px] text-muted-foreground mb-0.5 ml-1">
          {besked.afsender_navn}
        </span>
      )}
      <div
        className={`max-w-[82%] px-3 py-1.5 text-[13px] whitespace-pre-wrap break-words leading-snug ${
          erMin
            ? "bg-[#FF2A6D] text-white rounded-2xl rounded-br-sm"
            : "bg-secondary text-foreground rounded-2xl rounded-bl-sm"
        }`}
      >
        {besked.tekst}
      </div>
      <div className={`flex items-center gap-1 mt-0.5 mx-1 ${erMin ? "flex-row-reverse" : "flex-row"}`}>
        <span className="text-[10px] text-muted-foreground">
          {klokkeslet(besked.sendt)}
        </span>
        {erMin && (
          erGruppe ? (
            // Gruppe: vis læste avatarer med tooltip
            andreLæste.length > 0 ? (
              <span
                className="relative group flex items-center gap-0.5 cursor-default"
                title={tooltipTekst}
              >
                <CheckCheck size={12} className="text-[#FF2A6D]" />
                <span className="text-[10px] text-[#FF2A6D]">
                  Læst ({andreLæste.length})
                </span>
                {/* Tooltip */}
                <span className="pointer-events-none absolute bottom-full right-0 mb-1.5 hidden group-hover:flex flex-col items-end z-10">
                  <span className="bg-foreground text-background text-[11px] rounded-lg px-2.5 py-1.5 shadow-lg whitespace-nowrap max-w-[200px] leading-relaxed">
                    {læsteNavne.map((navn, i) => (
                      <span key={i} className="block">{navn}</span>
                    ))}
                  </span>
                  <span className="w-2 h-2 bg-foreground rotate-45 -mt-1 mr-2" />
                </span>
              </span>
            ) : (
              <span className="flex items-center gap-0.5 text-[10px] text-muted-foreground">
                <Check size={12} />
                <span>Sendt</span>
              </span>
            )
          ) : (
            // DM: simpel læst/sendt
            <span className="flex items-center gap-0.5 text-[10px] text-muted-foreground">
              {erLæst ? (
                <>
                  <CheckCheck size={12} className="text-[#FF2A6D]" />
                  <span className="text-[10px] text-[#FF2A6D]">Læst</span>
                </>
              ) : (
                <>
                  <Check size={12} />
                  <span>Sendt</span>
                </>
              )}
            </span>
          )
        )}
      </div>
    </div>
  );
}

// ─── Kanal-item ───────────────────────────────────────────────────────────────

function KanalItem({ kanal, onClick }) {
  const initial = (kanal.navn || "?").charAt(0).toUpperCase();
  return (
    <button
      onClick={onClick}
      className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-left transition-colors hover:bg-secondary/60"
    >
      <div className="shrink-0 w-8 h-8 rounded-full bg-secondary flex items-center justify-center text-xs font-bold text-muted-foreground">
        {kanal.type === "gruppe" ? <Users size={13} /> : initial}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-1">
          <span className="truncate text-[13px] text-foreground font-medium">
            {kanal.navn}
          </span>
          <span className="text-[10px] text-muted-foreground shrink-0">
            {sidstAktivLabel(kanal.sidst_aktiv)}
          </span>
        </div>
      </div>
      {kanal.ulaest_antal > 0 && (
        <span className="shrink-0 min-w-[16px] h-4 px-1 bg-[#FF2A6D] text-white text-[10px] font-bold rounded-full flex items-center justify-center">
          {kanal.ulaest_antal > 99 ? "99+" : kanal.ulaest_antal}
        </span>
      )}
    </button>
  );
}

// ─── Ny samtale view ─────────────────────────────────────────────────────────

function NySamtaleView({ token, onBack, onOpret }) {
  const [brugere, setBrugere] = useState([]);
  const [loading, setLoading] = useState(true);
  const [søg, setSøg] = useState("");
  const [valgte, setValgte] = useState([]);
  const [gruppenavn, setGruppenavn] = useState("");
  const [opretter, setOpretter] = useState(false);
  const søgRef = useRef(null);

  useEffect(() => {
    if (!token) return;
    setLoading(true);
    axios
      .get(`${API}/chat/brugere`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => {
        const med = (r.data || []).filter((b) => b.email && b.navn);
        setBrugere(med);
      })
      .catch((err) => {
        console.error("chat/brugere fejl:", err?.response?.status, err?.message);
        toast.error("Kunne ikke hente brugere");
      })
      .finally(() => setLoading(false));
  }, [token]);

  useEffect(() => {
    setTimeout(() => søgRef.current?.focus(), 80);
  }, []);

  const filtrerede = brugere.filter(
    (b) =>
      b.navn?.toLowerCase().includes(søg.toLowerCase()) ||
      b.email?.toLowerCase().includes(søg.toLowerCase()) ||
      b.afdeling?.toLowerCase().includes(søg.toLowerCase())
  );

  const toggle = (b) =>
    setValgte((prev) =>
      prev.find((v) => v.email === b.email)
        ? prev.filter((v) => v.email !== b.email)
        : [...prev, b]
    );

  const erGruppe = valgte.length > 1;

  const handleOpret = async () => {
    if (!valgte.length) return toast.error("Vælg mindst én bruger");
    if (erGruppe && !gruppenavn.trim()) return toast.error("Angiv et gruppenavn");
    setOpretter(true);
    try {
      const { data } = await axios.post(
        `${API}/chat/kanaler`,
        {
          navn: erGruppe ? gruppenavn.trim() : valgte[0].navn,
          type: erGruppe ? "gruppe" : "dm",
          medlemmer: valgte.map((v) => v.email),
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      onOpret(data);
    } catch (err) {
      console.error("opret kanal fejl:", err?.response?.data, err?.message);
      toast.error("Kunne ikke oprette samtale");
      setOpretter(false);
    }
  };

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center gap-2 px-4 py-3 border-b border-border shrink-0">
        <button
          onClick={onBack}
          className="text-muted-foreground hover:text-foreground p-1 -ml-1 rounded transition-colors"
        >
          <ArrowLeft size={16} />
        </button>
        <span className="font-semibold text-sm">Ny besked</span>
        <span className="ml-auto text-[11px] text-muted-foreground">
          {loading ? "" : `${brugere.length} brugere`}
        </span>
      </div>

      <div className="px-4 pt-3 pb-2 shrink-0">
        <div className="relative">
          <Search
            size={13}
            className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none"
          />
          <input
            ref={søgRef}
            value={søg}
            onChange={(e) => setSøg(e.target.value)}
            placeholder="Søg navn, e-mail eller afdeling..."
            className="w-full pl-7 pr-3 py-2 text-[13px] bg-secondary border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF2A6D]/30"
          />
        </div>
      </div>

      {valgte.length > 0 && (
        <div className="px-4 pb-2 flex flex-wrap gap-1 shrink-0">
          {valgte.map((v) => (
            <span
              key={v.email}
              className="inline-flex items-center gap-1 bg-[#FF2A6D]/10 text-[#FF2A6D] text-xs rounded-full px-2 py-0.5"
            >
              {v.navn}
              <button onClick={() => toggle(v)} className="hover:text-[#e0245f]">
                <X size={10} />
              </button>
            </span>
          ))}
        </div>
      )}

      {erGruppe && (
        <div className="px-4 pb-2 shrink-0">
          <input
            value={gruppenavn}
            onChange={(e) => setGruppenavn(e.target.value)}
            placeholder="Gruppenavn..."
            className="w-full px-3 py-2 text-[13px] bg-secondary border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF2A6D]/30"
          />
        </div>
      )}

      <div className="flex-1 overflow-y-auto px-3 py-1">
        {loading ? (
          <div className="flex items-center justify-center py-10 gap-2 text-muted-foreground">
            <Loader2 size={16} className="animate-spin" />
            <span className="text-[13px]">Henter brugere...</span>
          </div>
        ) : filtrerede.length === 0 ? (
          <p className="text-[13px] text-muted-foreground text-center py-8">
            {søg ? `Ingen resultater for "${søg}"` : "Ingen brugere fundet"}
          </p>
        ) : (
          filtrerede.map((b) => {
            const valgt = valgte.find((v) => v.email === b.email);
            return (
              <button
                key={b.email}
                onClick={() => toggle(b)}
                className={`w-full flex items-center gap-2.5 px-2 py-2 rounded-lg text-left transition-colors mb-0.5 ${
                  valgt ? "bg-[#FF2A6D]/10" : "hover:bg-secondary/60"
                }`}
              >
                <div
                  className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${
                    valgt ? "bg-[#FF2A6D] text-white" : "bg-secondary text-muted-foreground"
                  }`}
                >
                  {(b.navn || "?").charAt(0).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] font-medium truncate">{b.navn}</div>
                  {b.afdeling && (
                    <div className="text-[11px] text-muted-foreground">{b.afdeling}</div>
                  )}
                </div>
                {valgt && (
                  <div className="w-4 h-4 rounded-full bg-[#FF2A6D] flex items-center justify-center shrink-0">
                    <svg viewBox="0 0 10 8" className="w-2.5 h-2.5">
                      <path
                        d="M1 4l3 3 5-6"
                        stroke="white"
                        strokeWidth="1.5"
                        fill="none"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                )}
              </button>
            );
          })
        )}
      </div>

      <div className="px-4 py-3 border-t border-border shrink-0">
        <button
          onClick={handleOpret}
          disabled={opretter || !valgte.length || (erGruppe && !gruppenavn.trim())}
          className="w-full py-2 rounded-lg bg-[#FF2A6D] text-white text-[13px] font-semibold hover:bg-[#e0245f] disabled:opacity-40 transition-colors flex items-center justify-center gap-2"
        >
          {opretter && <Loader2 size={14} className="animate-spin" />}
          {erGruppe ? "Opret gruppe" : "Start samtale"}
        </button>
      </div>
    </div>
  );
}

// ─── Notifikationer view ──────────────────────────────────────────────────────

function NotifikationerView({ token }) {
  const { setUlæstNotifikationer } = useNotifikationer();
  const [notifikationer, setNotifikationer] = useState([]);
  const [loading, setLoading] = useState(true);
  const [markererAlle, setMarkererAlle] = useState(false);

  const hentNotifikationer = useCallback(async () => {
    if (!token) return;
    try {
      const { data } = await axios.get(`${API}/notifikationer`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setNotifikationer(data || []);
    } catch {
      /* stille */
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    hentNotifikationer();
  }, [hentNotifikationer]);

  const markerLaest = async (id) => {
    try {
      await axios.post(`${API}/notifikationer/${id}/laest`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setNotifikationer((prev) => prev.map((n) => (n._id === id ? { ...n, laest: true } : n)));
      setUlæstNotifikationer((c) => Math.max(0, c - 1));
    } catch {
      /* stille */
    }
  };

  const markerAlleLaest = async () => {
    setMarkererAlle(true);
    try {
      await axios.post(`${API}/notifikationer/laes-alle`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setNotifikationer((prev) => prev.map((n) => ({ ...n, laest: true })));
      setUlæstNotifikationer(0);
    } catch {
      /* stille */
    } finally {
      setMarkererAlle(false);
    }
  };

  const ulæste = notifikationer.filter((n) => !n.laest).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (notifikationer.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-muted-foreground gap-2">
        <Bell size={28} className="opacity-20" />
        <p className="text-[13px]">Ingen notifikationer</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {ulæste > 0 && (
        <div className="flex justify-end px-4 py-2 shrink-0 border-b border-border">
          <button
            onClick={markerAlleLaest}
            disabled={markererAlle}
            className="flex items-center gap-1.5 text-[12px] text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50"
          >
            {markererAlle ? (
              <Loader2 className="w-3 h-3 animate-spin" />
            ) : (
              <CheckCheck className="w-3 h-3" />
            )}
            Markér alle som læst
          </button>
        </div>
      )}
      <div className="flex-1 overflow-y-auto px-3 py-2 flex flex-col gap-1.5">
        {notifikationer.map((n) => (
          <div
            key={n._id}
            className={`flex items-start gap-3 p-3 rounded-xl border transition-colors ${
              n.laest ? "bg-background border-border" : "bg-[#FF2A6D]/5 border-[#FF2A6D]/20"
            }`}
          >
            <span className="text-lg shrink-0 mt-0.5">{NOTIF_IKON[n.type] || "🔔"}</span>
            <div className="flex-1 min-w-0">
              <p className="text-[13px] font-medium leading-snug">{n.titel}</p>
              {n.tekst && <p className="text-[12px] text-muted-foreground mt-0.5">{n.tekst}</p>}
              <p className="text-[11px] text-muted-foreground mt-1">{fmtTid(n.oprettet)}</p>
            </div>
            {!n.laest && (
              <button
                onClick={() => markerLaest(n._id)}
                className="shrink-0 p-1 rounded-lg hover:bg-secondary transition-colors"
                title="Markér som læst"
              >
                <Check className="w-3 h-3 text-muted-foreground" />
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Hoved ChatPanel ──────────────────────────────────────────────────────────

export default function ChatPanel({ open, onClose }) {
  const { user, token } = useAuth();
  const { sisteEvent, setUlæstBeskeder, ulæstBeskeder, ulæstNotifikationer } = useNotifikationer();

  const [fane, setFane] = useState("beskeder");
  const [visning, setVisning] = useState("liste");
  const [kanaler, setKanaler] = useState([]);
  const [aktivKanal, setAktivKanal] = useState(null);
  const [beskeder, setBeskeder] = useState([]);
  const [tekst, setTekst] = useState("");
  const [sender, setSender] = useState(false);
  const [henterAeldre, setHenterAeldre] = useState(false);
  const [harFlereBeskeder, setHarFlereBeskeder] = useState(false);
  const [søg, setSøg] = useState("");

  const beskedContainerRef = useRef(null);
  const scrollAnkerRef = useRef(null);
  const inputRef = useRef(null);
  const panelRef = useRef(null);
  const openRef = useRef(open);
  useEffect(() => { openRef.current = open; }, [open]);

  // Bloker scroll når panelet er åbent
  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    if (open) {
      // Mål scrollbar-bredde og kompensér så siden ikke hopper
      const scrollbarW = window.innerWidth - document.documentElement.clientWidth;
      if (window.innerWidth < 768) {
        // iOS: kræver position:fixed på body for at stoppe scroll
        const scrollY = window.scrollY;
        body.style.position = "fixed";
        body.style.top = `-${scrollY}px`;
        body.style.width = "100%";
      } else if (scrollbarW > 0) {
        html.style.paddingRight = `${scrollbarW}px`;
      }
      html.style.overflow = "hidden";
      body.style.overflow = "hidden";
    } else {
      const top = body.style.top;
      html.style.overflow = "";
      html.style.paddingRight = "";
      body.style.position = "";
      body.style.top = "";
      body.style.width = "";
      body.style.overflow = "";
      if (top) window.scrollTo(0, -parseInt(top || "0", 10));
    }
    return () => {
      const top = body.style.top;
      html.style.overflow = "";
      html.style.paddingRight = "";
      body.style.position = "";
      body.style.top = "";
      body.style.width = "";
      body.style.overflow = "";
      if (top) window.scrollTo(0, -parseInt(top || "0", 10));
    };
  }, [open]);

  // Nulstil til liste-visning når panelet lukkes
  useEffect(() => {
    if (!open) {
      setTimeout(() => {
        setVisning("liste");
        setAktivKanal(null);
        setBeskeder([]);
        setSøg("");
      }, 250);
    }
  }, [open]);

  // Luk ved klik udenfor — kun på desktop (mobil fylder hele skærmen)
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (window.innerWidth >= 768 && panelRef.current && !panelRef.current.contains(e.target)) {
        onClose();
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open, onClose]);

  // Hent kanaler når panel åbner
  const hentKanaler = useCallback(async () => {
    if (!token) return;
    try {
      const { data } = await axios.get(`${API}/chat/kanaler`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setKanaler(data);
      const total = (data || []).reduce((s, k) => s + (k.ulaest_antal || 0), 0);
      setUlæstBeskeder(total);
    } catch {
      /* stille */
    }
  }, [token, setUlæstBeskeder]);

  useEffect(() => {
    if (open) hentKanaler();
  }, [open, hentKanaler]);

  // Hent beskeder
  const hentBeskeder = useCallback(
    async (kanal, before = null) => {
      if (!kanal) return;
      try {
        const { data } = await axios.get(
          `${API}/chat/kanaler/${kanal._id}/meldinger`,
          {
            headers: { Authorization: `Bearer ${token}` },
            params: before ? { before } : {},
          }
        );
        if (before) {
          setBeskeder((prev) => [...data, ...prev]);
        } else {
          setBeskeder(data);
        }
        setHarFlereBeskeder(data.length >= 30);
        axios
          .post(
            `${API}/chat/kanaler/${kanal._id}/laest`,
            {},
            { headers: { Authorization: `Bearer ${token}` } }
          )
          .then(() => {
            setKanaler((prev) =>
              prev.map((k) => (k._id === kanal._id ? { ...k, ulaest_antal: 0 } : k))
            );
            setUlæstBeskeder((prev) => Math.max(0, prev - (kanal.ulaest_antal || 0)));
          })
          .catch(() => {});
      } catch {
        toast.error("Kunne ikke hente beskeder");
      }
    },
    [token, setUlæstBeskeder]
  );

  useEffect(() => {
    if (aktivKanal) {
      setBeskeder([]);
      hentBeskeder(aktivKanal);
    }
  }, [aktivKanal, hentBeskeder]);

  // Auto-scroll til bund
  useEffect(() => {
    scrollAnkerRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [beskeder]);

  // Fokus på input når kanal åbner
  useEffect(() => {
    if (visning === "kanal") setTimeout(() => inputRef.current?.focus(), 50);
  }, [visning]);

  // Diskret ping-lyd via Web Audio API
  const pingLyd = () => {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.type = "sine";
      osc.frequency.setValueAtTime(880, ctx.currentTime);
      osc.frequency.exponentialRampToValueAtTime(660, ctx.currentTime + 0.15);
      gain.gain.setValueAtTime(0.12, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.3);
      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + 0.3);
    } catch { /* stille */ }
  };

  // SSE realtid
  useEffect(() => {
    if (!sisteEvent) return;

    if (sisteEvent.type === "besked") {
      const melding = sisteEvent.melding || sisteEvent;
      if (aktivKanal && sisteEvent.kanal_id === aktivKanal._id) {
        setBeskeder((prev) =>
          prev.find((b) => b._id === melding._id) ? prev : [...prev, melding]
        );
        axios
          .post(
            `${API}/chat/kanaler/${aktivKanal._id}/laest`,
            {},
            { headers: { Authorization: `Bearer ${token}` } }
          )
          .catch(() => {});
      } else {
        setKanaler((prev) =>
          prev.map((k) =>
            k._id === sisteEvent.kanal_id
              ? { ...k, ulaest_antal: (k.ulaest_antal || 0) + 1, sidst_aktiv: melding.sendt }
              : k
          )
        );
        // Ping kun når: besked fra anden, chat-panel lukket, side aktiv i browser
        if (
          melding.afsender_email !== user?.email &&
          !openRef.current &&
          document.visibilityState === "visible"
        ) {
          pingLyd();
        }
      }
    }

    if (sisteEvent.type === "besked_laest") {
      const { melding_ids, laest_af, laest_tidspunkt } = sisteEvent;
      if (melding_ids && laest_af) {
        setBeskeder((prev) =>
          prev.map((b) =>
            melding_ids.includes(b._id)
              ? {
                  ...b,
                  laest_af: b.laest_af
                    ? [...new Set([...b.laest_af, laest_af])]
                    : [laest_af],
                  laest_tidspunkt,
                }
              : b
          )
        );
      }
    }

    if (sisteEvent.type === "ny_kanal") {
      const nyKanal = sisteEvent.kanal;
      if (nyKanal) {
        setKanaler((prev) =>
          prev.find((k) => k._id === nyKanal._id) ? prev : [nyKanal, ...prev]
        );
      }
    }
  }, [sisteEvent, aktivKanal, token]);

  // Send besked
  const sendBesked = async () => {
    const txt = tekst.trim();
    if (!txt || !aktivKanal || sender) return;
    setSender(true);
    try {
      const { data } = await axios.post(
        `${API}/chat/send`,
        { kanal_id: aktivKanal._id, tekst: txt },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setBeskeder((prev) => [...prev, data]);
      setTekst("");
      setKanaler((prev) =>
        prev.map((k) => (k._id === aktivKanal._id ? { ...k, sidst_aktiv: data.sendt } : k))
      );
    } catch {
      toast.error("Beskeden kunne ikke sendes");
    } finally {
      setSender(false);
      inputRef.current?.focus();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendBesked();
    }
  };

  const hentAeldre = async () => {
    if (!aktivKanal || !beskeder.length || henterAeldre) return;
    setHenterAeldre(true);
    const scrollEl = beskedContainerRef.current;
    const gamleSH = scrollEl?.scrollHeight || 0;
    await hentBeskeder(aktivKanal, beskeder[0]._id);
    setHenterAeldre(false);
    if (scrollEl) scrollEl.scrollTop = scrollEl.scrollHeight - gamleSH;
  };

  const åbnKanal = (kanal) => {
    setAktivKanal(kanal);
    setVisning("kanal");
  };

  const handleNyKanal = (kanal) => {
    setKanaler((prev) => (prev.find((k) => k._id === kanal._id) ? prev : [kanal, ...prev]));
    åbnKanal(kanal);
  };

  const dmKanaler = kanaler
    .filter(
      (k) =>
        k.type === "dm" &&
        (!søg || k.navn?.toLowerCase().includes(søg.toLowerCase()))
    )
    .sort((a, b) => new Date(b.sidst_aktiv || 0) - new Date(a.sidst_aktiv || 0));
  const gruppeKanaler = kanaler
    .filter(
      (k) =>
        k.type === "gruppe" &&
        (!søg || k.navn?.toLowerCase().includes(søg.toLowerCase()))
    )
    .sort((a, b) => new Date(b.sidst_aktiv || 0) - new Date(a.sidst_aktiv || 0));

  function renderBeskeder() {
    const items = [];
    let sidsteDato = null;
    beskeder.forEach((b) => {
      const dato = new Date(b.sendt).toDateString();
      if (dato !== sidsteDato) {
        sidsteDato = dato;
        items.push(<DatoSep key={`sep-${b._id}`} label={datoLabel(b.sendt)} />);
      }
      items.push(
        <Boble
          key={b._id}
          besked={b}
          erMin={b.afsender_email === user?.email}
          minEmail={user?.email}
          kanalMedlemmer={aktivKanal?.medlemmer || []}
          kanalMedlemmerNavne={aktivKanal?.medlemmer_navne || []}
          erGruppe={aktivKanal?.type === "gruppe"}
        />
      );
    });
    return items;
  }

  const ulæstTotal = kanaler.reduce((s, k) => s + (k.ulaest_antal || 0), 0);

  // ── Render ───────────────────────────────────────────────────────────────────

  return (
    <>
      <div
        ref={panelRef}
        data-open={open ? "true" : "false"}
        data-wide="true"
        className="chat-panel fixed z-50 flex flex-col bg-background shadow-2xl inset-0 md:inset-auto md:top-0 md:right-0 md:h-full md:border-l md:border-border"
      >
      {/* ── KANAL-VIEW (beskeder) ─────────────────────────────────── */}
      {visning === "kanal" && aktivKanal ? (
        <>
          <div className="flex items-center gap-2 px-3 py-3 border-b border-border shrink-0">
            <button
              onClick={() => { setVisning("liste"); setAktivKanal(null); setBeskeder([]); }}
              className="p-1 rounded hover:bg-secondary text-muted-foreground hover:text-foreground transition-colors"
            >
              <ArrowLeft size={16} />
            </button>
            <div className="w-7 h-7 rounded-full bg-[#FF2A6D]/10 flex items-center justify-center shrink-0">
              {aktivKanal.type === "gruppe" ? (
                <Users size={13} className="text-[#FF2A6D]" />
              ) : (
                <span className="text-[#FF2A6D] text-xs font-bold">
                  {(aktivKanal.navn || "?").charAt(0).toUpperCase()}
                </span>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[13px] font-semibold truncate">{aktivKanal.navn}</div>
              {aktivKanal.type === "gruppe" && (
                <div className="text-[10px] text-muted-foreground truncate">
                  {(aktivKanal.medlemmer_navne || aktivKanal.medlemmer || []).slice(0, 3).join(", ")}
                  {(aktivKanal.medlemmer || []).length > 3
                    ? ` +${aktivKanal.medlemmer.length - 3}`
                    : ""}
                </div>
              )}
            </div>
            <button
              onClick={onClose}
              className="p-1.5 rounded-lg hover:bg-secondary transition-colors text-muted-foreground hover:text-foreground shrink-0"
            >
              <X size={14} />
            </button>
          </div>

          <div ref={beskedContainerRef} className="flex-1 overflow-y-auto px-3 py-3">
            {harFlereBeskeder && (
              <div className="flex justify-center mb-3">
                <button
                  onClick={hentAeldre}
                  disabled={henterAeldre}
                  className="flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground bg-secondary px-3 py-1 rounded-full transition-colors disabled:opacity-50"
                >
                  {henterAeldre ? (
                    <Loader2 size={11} className="animate-spin" />
                  ) : (
                    <ChevronDown size={11} className="rotate-180" />
                  )}
                  Hent ældre
                </button>
              </div>
            )}
            {beskeder.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-muted-foreground py-8">
                <MessageSquare size={28} className="mb-2 opacity-20" />
                <p className="text-[13px]">Ingen beskeder endnu</p>
              </div>
            ) : (
              renderBeskeder()
            )}
            <div ref={scrollAnkerRef} />
          </div>

          <div className="px-4 pt-3 pb-safe border-t border-border bg-card shrink-0">
            <div className="flex items-end gap-2">
              <textarea
                ref={inputRef}
                value={tekst}
                onChange={(e) => setTekst(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Skriv en besked..."
                rows={1}
                className="flex-1 resize-none px-3 py-2 text-[13px] bg-secondary border border-border rounded-xl focus:outline-none focus:ring-2 focus:ring-[#FF2A6D]/30 max-h-24 overflow-y-auto"
                style={{ minHeight: "38px" }}
                onInput={(e) => {
                  e.target.style.height = "auto";
                  e.target.style.height = Math.min(e.target.scrollHeight, 96) + "px";
                }}
              />
              <button
                onClick={sendBesked}
                disabled={!tekst.trim() || sender}
                className="shrink-0 w-12 rounded-xl bg-[#FF2A6D] text-white flex items-center justify-center hover:bg-[#e0245f] disabled:opacity-40 transition-colors self-stretch"
              >
                {sender ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
              </button>
            </div>
          </div>
        </>
      ) : visning === "ny" ? (
        <NySamtaleView
          token={token}
          onBack={() => setVisning("liste")}
          onOpret={handleNyKanal}
        />
      ) : (
        <>
          <div className="flex items-center justify-between px-4 py-3 border-b border-border shrink-0">
            <span className="font-semibold text-sm">Indbakke</span>
            <button
              onClick={onClose}
              className="p-1.5 rounded-lg hover:bg-secondary transition-colors text-muted-foreground hover:text-foreground"
            >
              <X size={15} />
            </button>
          </div>

          <div className="flex items-center px-4 pt-2 pb-0 gap-1 shrink-0">
            <button
              onClick={() => setFane("beskeder")}
              className={`relative flex items-center gap-1.5 px-3 py-2 text-[13px] font-medium rounded-t-lg transition-colors border-b-2 ${
                fane === "beskeder"
                  ? "border-[#FF2A6D] text-[#FF2A6D]"
                  : "border-transparent text-muted-foreground hover:text-foreground"
              }`}
            >
              <MessageSquare size={13} />
              Beskeder
              {ulæstTotal > 0 && (
                <span className="bg-[#FF2A6D] text-white text-[9px] font-bold px-1 py-0.5 rounded-full leading-none min-w-[14px] text-center">
                  {ulæstTotal > 99 ? "99+" : ulæstTotal}
                </span>
              )}
            </button>
            <button
              onClick={() => setFane("notifikationer")}
              className={`relative flex items-center gap-1.5 px-3 py-2 text-[13px] font-medium rounded-t-lg transition-colors border-b-2 ${
                fane === "notifikationer"
                  ? "border-[#FF2A6D] text-[#FF2A6D]"
                  : "border-transparent text-muted-foreground hover:text-foreground"
              }`}
            >
              <Bell size={13} />
              Notifikationer
              {ulæstNotifikationer > 0 && (
                <span className="bg-[#FF2A6D] text-white text-[9px] font-bold px-1 py-0.5 rounded-full leading-none min-w-[14px] text-center">
                  {ulæstNotifikationer > 99 ? "99+" : ulæstNotifikationer}
                </span>
              )}
            </button>
          </div>
          <div className="border-b border-border shrink-0" />

          {fane === "beskeder" && (
            <>
              <div className="flex items-center gap-2 px-3 py-2 shrink-0">
                <div className="relative flex-1">
                  <Search
                    size={12}
                    className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none"
                  />
                  <input
                    value={søg}
                    onChange={(e) => setSøg(e.target.value)}
                    placeholder="Søg samtaler..."
                    className="w-full pl-7 pr-2 py-1.5 text-xs bg-secondary border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF2A6D]/30"
                  />
                </div>
                <button
                  onClick={() => setVisning("ny")}
                  className="shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#FF2A6D] text-white text-xs font-semibold hover:bg-[#e0245f] transition-colors"
                >
                  <Edit3 size={13} /> Ny besked
                </button>
              </div>

              <div className="flex-1 overflow-y-auto px-2 py-1">
                {dmKanaler.length === 0 && gruppeKanaler.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-12 text-muted-foreground gap-2">
                    <MessageSquare size={28} className="opacity-20" />
                    <p className="text-[13px]">Ingen samtaler endnu</p>
                    <button
                      onClick={() => setVisning("ny")}
                      className="mt-1 flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#FF2A6D] text-white text-xs font-semibold hover:bg-[#e0245f] transition-colors"
                    >
                      <Plus size={13} /> Ny besked
                    </button>
                  </div>
                ) : (
                  <>
                    {dmKanaler.length > 0 && (
                      <div className="mb-2">
                        <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider px-3 py-1.5">
                          Direkte beskeder
                        </p>
                        {dmKanaler.map((k) => (
                          <KanalItem key={k._id} kanal={k} onClick={() => åbnKanal(k)} />
                        ))}
                      </div>
                    )}
                    {gruppeKanaler.length > 0 && (
                      <div>
                        <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider px-3 py-1.5">
                          Grupper
                        </p>
                        {gruppeKanaler.map((k) => (
                          <KanalItem key={k._id} kanal={k} onClick={() => åbnKanal(k)} />
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
            </>
          )}

          {fane === "notifikationer" && (
            <div className="flex-1 overflow-y-auto">
              <NotifikationerView token={token} />
            </div>
          )}
        </>
      )}
      </div>
    </>
  );
}
